import { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldCheck, Save, Pencil, AlertTriangle, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loading } from "@/components/StateViews";
import ConfirmDialog from "@/components/ConfirmDialog";
import { fmtDateTime } from "@/lib/format";

// ---------- Roles & Default Access (admin-editable) ----------
export function RolesTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});           // { role: Set(perms) }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/access/permissions").then(({ data }) => setData(data.data)).catch((e) => setErr(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (!data) return <Loading label="Loading roles..." />;
  const { permissions, groups, role_defaults, editable_roles = ["Sales Manager", "Sales Executive"] } = data;
  const roles = Object.keys(role_defaults);
  const isEditable = (r) => editable_roles.includes(r);

  const startEdit = () => {
    const d = {};
    editable_roles.forEach((r) => { d[r] = new Set(role_defaults[r] || []); });
    setDraft(d);
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft({}); };

  const has = (r, k) => (editing && isEditable(r)) ? !!draft[r]?.has(k) : (role_defaults[r] || []).includes(k);
  const toggle = (r, k) => {
    setDraft((prev) => {
      const next = { ...prev };
      const s = new Set(next[r]);
      s.has(k) ? s.delete(k) : s.add(k);
      next[r] = s;
      return next;
    });
  };

  const changedRoles = editable_roles.filter((r) => {
    const orig = new Set(role_defaults[r] || []);
    const cur = draft[r] || new Set();
    if (orig.size !== cur.size) return true;
    for (const p of cur) if (!orig.has(p)) return true;
    return false;
  });

  const doSave = async () => {
    setSaving(true);
    try {
      for (const r of changedRoles) {
        await api.put("/access/roles", { role: r, permissions: [...draft[r]] });
      }
      toast.success("Role access updated — manual overrides for changed roles were reset.");
      setConfirmOpen(false); setEditing(false); setDraft({});
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="roles-tab">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-ink/60">Default permissions granted to each role. <strong>Admin</strong> always has full access. Editing a role here applies to <strong>every</strong> user of that role.</p>
        {!editing ? (
          <button onClick={startEdit} data-testid="roles-edit-btn" title="Edit role access" className="flex shrink-0 items-center gap-1.5 rounded-lg border border-sky bg-white px-3 py-2 text-sm font-semibold text-loop hover:bg-sky/30">
            <Pencil className="h-4 w-4" />Edit
          </button>
        ) : (
          <div className="flex shrink-0 items-center gap-2">
            <button onClick={cancelEdit} data-testid="roles-cancel-btn" className="flex items-center gap-1.5 rounded-lg border border-sky bg-white px-3 py-2 text-sm font-semibold text-ink/60 hover:bg-mist"><X className="h-4 w-4" />Cancel</button>
            <button onClick={() => changedRoles.length ? setConfirmOpen(true) : toast.info("No changes to save")} disabled={!changedRoles.length} data-testid="roles-save-btn" className="flex items-center gap-1.5 rounded-lg bg-loop px-3 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-50"><Save className="h-4 w-4" />Save changes</button>
          </div>
        )}
      </div>

      {editing && (
        <div data-testid="roles-edit-warning" className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Editing a role's access overrides <strong>all manual (Custom Access) permissions</strong> you've granted to individual users of that role. On save, those users are reset to the new role defaults.</span>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-sky/70 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-mist text-left text-xs uppercase tracking-wide text-ink/50">
            <tr><th className="px-4 py-3">Permission</th>{roles.map((r) => <th key={r} className="px-4 py-3 text-center">{r}{editing && isEditable(r) && <span className="ml-1 text-[9px] font-bold text-loop">editing</span>}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-sky/50">
            {Object.entries(groups).map(([group, keys]) => (
              <>
                <tr key={group} className="bg-sky/10"><td colSpan={roles.length + 1} className="px-4 py-2 font-sora text-xs font-bold text-navy">{group}</td></tr>
                {keys.map((k) => (
                  <tr key={k} data-testid={`role-perm-row-${k}`}>
                    <td className="px-4 py-2 text-ink/80">{permissions[k]}</td>
                    {roles.map((r) => (
                      <td key={r} className="px-4 py-2 text-center">
                        {editing && isEditable(r) ? (
                          <input type="checkbox" data-testid={`role-toggle-${r}-${k}`} checked={!!has(r, k)} onChange={() => toggle(r, k)} className="h-4 w-4 accent-loop" />
                        ) : has(r, k)
                          ? <span className="text-emerald-600">✓</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Apply role access to all users?"
        description={`This updates the default access for ${changedRoles.join(" & ")} and RESETS every manual Custom Access override for those roles. All affected users will match the new role defaults. This cannot be undone.`}
        confirmLabel={saving ? "Saving…" : "Apply to all"}
        testid="roles-save-confirm"
        onConfirm={doSave}
      />
    </div>
  );
}

// ---------- Custom Access (per-user overrides) ----------
export function CustomAccessTab({ users }) {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState("");
  const [detail, setDetail] = useState(null);
  const [added, setAdded] = useState(new Set());
  const [removed, setRemoved] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/access/permissions").then(({ data }) => setCatalog(data.data)).catch((e) => toast.error(apiError(e))); }, []);

  const loadUser = useCallback(async (uid) => {
    if (!uid) { setDetail(null); return; }
    try {
      const { data } = await api.get(`/access/users/${uid}`);
      setDetail(data.data);
      setAdded(new Set(data.data.custom_added));
      setRemoved(new Set(data.data.custom_removed));
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  const nonAdmins = (users || []).filter((u) => u.role !== "Admin");

  const isDefault = (perm) => (detail?.role_defaults || []).includes(perm);
  const isEffective = (perm) => {
    if (added.has(perm)) return true;
    if (removed.has(perm)) return false;
    return isDefault(perm);
  };
  const toggle = (perm) => {
    const def = isDefault(perm);
    const a = new Set(added); const r = new Set(removed);
    if (isEffective(perm)) {
      // turning OFF
      if (def) { r.add(perm); a.delete(perm); } else { a.delete(perm); }
    } else {
      // turning ON
      if (def) { r.delete(perm); } else { a.add(perm); }
    }
    setAdded(a); setRemoved(r);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/access/users/${selected}`, { added: [...added], removed: [...removed] });
      toast.success("Access updated");
      loadUser(selected);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="custom-access-tab">
      <p className="text-sm text-ink/60">Grant extra access or remove default access for a specific user. Effective = role defaults + added − removed.</p>
      <div className="max-w-sm">
        <label className="mb-1 block text-xs font-medium text-ink/50">Select a user</label>
        <select data-testid="custom-access-user-select" value={selected} onChange={(e) => { setSelected(e.target.value); loadUser(e.target.value); }} className="h-10 w-full rounded-lg border border-sky bg-white px-2 text-sm outline-none focus:border-loop">
          <option value="">— Choose a user —</option>
          {nonAdmins.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
        </select>
      </div>

      {detail && catalog && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 rounded-xl bg-mist px-3 py-2 text-sm">
            <ShieldCheck className="h-4 w-4 text-loop" />
            <span className="font-semibold text-navy">{detail.name}</span>
            <span className="rounded-full bg-sky/60 px-2 py-0.5 text-xs text-navy">{detail.role}</span>
          </div>
          {Object.entries(catalog.groups).map(([group, keys]) => (
            <div key={group} className="rounded-2xl border border-sky/70 bg-white p-3">
              <p className="mb-2 font-sora text-xs font-bold text-navy">{group}</p>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {keys.map((k) => {
                  const eff = isEffective(k);
                  const def = isDefault(k);
                  return (
                    <label key={k} data-testid={`access-perm-${k}`} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-mist">
                      <input type="checkbox" data-testid={`access-toggle-${k}`} checked={eff} onChange={() => toggle(k)} className="h-4 w-4 accent-loop" />
                      <span className="text-ink/80">{catalog.permissions[k]}</span>
                      {def && <span className="rounded bg-sky/40 px-1.5 py-0.5 text-[9px] font-semibold text-navy">default</span>}
                      {added.has(k) && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">+added</span>}
                      {removed.has(k) && <span className="rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-semibold text-red-600">−removed</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          <button onClick={save} disabled={saving} data-testid="custom-access-save" className="flex items-center gap-2 rounded-xl bg-loop px-4 py-2.5 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save access
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Access Audit Log ----------
export function AuditTab() {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get("/access/audit").then(({ data }) => setEvents(data.data)).catch(() => setEvents([])); }, []);
  if (!events) return <Loading label="Loading audit log..." />;
  return (
    <div className="space-y-3" data-testid="audit-tab">
      <p className="text-sm text-ink/60">Every access change is recorded here.</p>
      {events.length === 0 ? <p className="rounded-xl border border-dashed border-sky p-6 text-center text-sm text-ink/40">No access changes recorded yet.</p> : (
        <div className="overflow-x-auto rounded-2xl border border-sky/70 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-mist text-left text-xs uppercase tracking-wide text-ink/50">
              <tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Changed by</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Permission</th><th className="px-4 py-3">Action</th></tr>
            </thead>
            <tbody className="divide-y divide-sky/50">
              {events.map((e, i) => (
                <tr key={i} data-testid={`audit-row-${i}`}>
                  <td className="whitespace-nowrap px-4 py-2 text-ink/60">{fmtDateTime(e.at)}</td>
                  <td className="px-4 py-2 text-navy">{e.changed_by_name}</td>
                  <td className="px-4 py-2 text-ink/80">{e.changed_for_name}</td>
                  <td className="px-4 py-2 font-mono text-xs text-ink/70">{e.permission}</td>
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${e.action === "added" ? "bg-emerald-50 text-emerald-700" : e.action === "removed" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"}`}>{e.action}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
